"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import Link from "next/link";
import toast from "react-hot-toast";
import thankYouService from "@/services/thank-you-service";
import ThankYouMessageCard from "@/app/components/appcomponents/ThankYouMessageCard";
import OrderFlow from "@/app/components/orders/OrderFlow";
import {
  SAMPLE_MESSAGES,
  SAMPLE_LIST_ITEMS,
} from "@/app/components/appcomponents/homeThanksSamples";

// 3 messages are shown (2 on phones), "Odpri naprej" opens 2 more at the bottom - never more.
const INITIAL_VISIBLE = 3;
const INITIAL_VISIBLE_MOBILE = 2;
const EXTRA_ON_REQUEST = 2;
const MAX_VISIBLE = INITIAL_VISIBLE + EXTRA_ON_REQUEST;
// "Zadnje dodano": desktop lists the 7 newest, including the ones shown on the left. Tablet and
// mobile are too small for that, so they skip the messages already shown and list the 6 before them.
const DESKTOP_LIST_SIZE = 7;
const SMALL_LIST_SIZE = 6;

// Colour of the label in the "Zadnje dodano" list follows the headline the author typed:
// "Zahvala" is green, "Spomin" is red, anything else is blue. The list shows it in capitals.
function headlineColor(headline) {
  const word = String(headline || "").trim().toLowerCase();
  if (word === "zahvala") return "text-[#318F6D]";
  if (word === "spomin") return "text-[#F07678]";
  return "text-[#256AA8]";
}

function LatestListItem({ item }) {
  const content = (
    <>
      <span className="block text-[16px] leading-6 uppercase text-[#1E2125]">{item.name}</span>
      <span
        className={`block text-right text-[12px] leading-[14px] mt-[9px] uppercase ${headlineColor(item.headline)}`}
      >
        {item.headline}
      </span>
      <span className="block text-right text-[12px] leading-[14px] mt-[1px] text-[#7C7C7C]">
        {item.authorName}
      </span>
    </>
  );
  return (
    <li className="border-b border-[#DFE2E2] py-[9px]">
      {item.slugKey ?
        <Link href={`/m/${item.slugKey}`} className="block">
          {content}
        </Link>
      : <div>{content}</div>}
    </li>
  );
}

export default function HomeThanksTab({ isMobile = false }) {
  const initialVisible = isMobile ? INITIAL_VISIBLE_MOBILE : INITIAL_VISIBLE;
  const [messages, setMessages] = useState(null);
  const [showMore, setShowMore] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const loadMessages = useCallback(async () => {
  try {
    const response = await thankYouService.getThankYouMessages();
    setMessages(response?.error ? [] : (response?.messages ?? []));
  } catch {
    setMessages([]);
    toast.error("Sporočil ni bilo mogoče naložiti");
  }
}, []);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Back from Stripe: ask the server to confirm the payment, then show the new message.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const messageId = params.get("thankYouMessage");
    const sessionId = params.get("session_id");
    const canceled = params.get("thankYouCanceled");
    if (!messageId && !canceled) return;

    const cleanUrl = () =>
      window.history.replaceState(null, "", window.location.pathname);

    if (canceled) {
      toast.error("Plačilo je bilo preklicano");
      cleanUrl();
      return;
    }
    if (messageId && sessionId) {
      thankYouService
        .confirmThankYouMessage(messageId, sessionId)
        .then(({ published }) => {
          if (published) {
            toast.success("Sporočilo je objavljeno");
            loadMessages();
          } else {
            toast.error("Plačilo še ni potrjeno");
          }
        })
        .catch(() => toast.error("Potrditev plačila ni uspela"))
        .finally(cleanUrl);
    }
  }, [loadMessages]);

  const realMessages = useMemo(() => messages ?? [], [messages]);

  // Example messages fill the slots real ones have not taken yet: each real message pushes out
  // the first example, then the next one, and so on.
  const cards = useMemo(() => {
    const real = realMessages.slice(0, MAX_VISIBLE);
    const padding = SAMPLE_MESSAGES.slice(real.length, INITIAL_VISIBLE);
    return [...real, ...padding];
  }, [realMessages]);

  const visibleCards = cards.slice(0, showMore ? initialVisible + EXTRA_ON_REQUEST : initialVisible);
  const canShowMore = !showMore && cards.length > initialVisible;

  // Example names fill the slots real messages have not taken yet.
  const [latestDesktop, latestSmall] = useMemo(() => {
  const toItem = (m) => ({
    id: m.id,
    headline: m.headline,
    name: m.Obituary ? `${m.Obituary.name} ${m.Obituary.sirName}` : "",
    authorName: m.authorName,
    slugKey: m.Obituary?.slugKey,
  });

  const fill = (real, size) => [
    ...real,
    ...SAMPLE_LIST_ITEMS.slice(0, size - real.length),
  ];

  const visibleCount = showMore
    ? initialVisible + EXTRA_ON_REQUEST
    : initialVisible;

  return [
    fill(
      realMessages.slice(0, DESKTOP_LIST_SIZE).map(toItem),
      DESKTOP_LIST_SIZE
    ),
    fill(
      realMessages
        .slice(visibleCount, visibleCount + SMALL_LIST_SIZE)
        .map(toItem),
      SMALL_LIST_SIZE
    ),
  ];
}, [realMessages, initialVisible, showMore]);

  // Candles lit on one card update the count on every card about the same person.
  const handleCandleBurned = (obituaryId) => {
    setMessages((prev) =>
      (prev ?? []).map((m) =>
        m.Obituary.id === obituaryId ?
          {
            ...m,
            Obituary: { ...m.Obituary, totalCandles: (m.Obituary.totalCandles ?? 0) + 1 },
          }
        : m
      )
    );
  };

  return (
    <div className="mt-[69px] tablet:mt-[46px] mobile:mt-[43px] mb-[60px] flex w-full flex-col items-center desktop:w-[951px] desktop:flex-row desktop:items-start desktop:gap-[63px]">
      <div className="flex w-full flex-col items-center desktop:w-[660px]">
        {visibleCards.map((message, index) => (
          <div key={message.id} className="flex w-full flex-col items-center">
            {index > 0 && (
              <div className="my-[48px] h-px w-[298px] max-w-full bg-[#D4D4D4]" aria-hidden="true" />
            )}
            <ThankYouMessageCard message={message} onCandleBurned={handleCandleBurned} />
          </div>
        ))}
      </div>

      <aside className="mt-[40px] w-full max-w-[524px] desktop:mt-0 desktop:w-[228px]">
        <div className="flex flex-col items-end [@media(max-width:739px)]:items-start">
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="h-10 w-[150px] rounded-lg bg-gradient-to-b from-[#0E9CE5] to-[#057DC3] text-[16px] text-white shadow-md"
          >
            Dodaj novo
          </button>
          <p className="mt-[9px] w-[228px] text-right [@media(max-width:739px)]:text-left text-[12px] leading-[18px] text-[#1E2125]">
            Zahvalo, posvetilo, spomin, obletnico, zadnji pozdrav
          </p>
        </div>

        <h3 className="mt-[44px] text-center [@media(max-width:739px)]:text-right text-[14px] font-bold leading-6 text-[#1E2125]">
          ZADNJE DODANO
        </h3>
        <ul className="mt-[14px] hidden border-t border-[#DFE2E2] desktop:block">
          {latestDesktop.map((item) => (
            <LatestListItem key={item.id} item={item} />
          ))}
        </ul>
        <ul className="mt-[14px] border-t border-[#DFE2E2] [@media(max-width:739px)]:ml-auto [@media(max-width:739px)]:w-[216px] tablet:grid tablet:grid-cols-2 tablet:gap-x-[71px] desktop:hidden">
          {latestSmall.map((item) => (
            <LatestListItem key={item.id} item={item} />
          ))}
        </ul>

        {canShowMore && (
          <div className="mt-4 text-right">
            <button
              type="button"
              onClick={() => setShowMore(true)}
              className="text-[14px] text-[#0A85C2]"
            >
              Odpri naprej
            </button>
          </div>
        )}
      </aside>

      {modalOpen && <OrderFlow product="thank_you" onClose={() => setModalOpen(false)} />}
    </div>
  );
}
